<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Button from '../../Button.svelte';
  import SearchInput from '../../SearchInput.svelte';
  import Table from '../../Table.svelte';
  import Pagination from '../../Pagination.svelte';
  import Dropdown from '../../Dropdown.svelte';
  import { employersService, type Employer, type EmployerFilters, type PaginationInfo } from '../../../lib/api/admin/employers.service';
  import toast from 'svelte-french-toast';
  import AddEmployerModal from './AddEmployerModal.svelte';
  import { createPreferencesManager } from '../../../lib/utils/preferences';
  import { apiPageToUiPage } from '../../../lib/utils/pagination';
  import LoadingSkeleton from '../../LoadingSkeleton.svelte';
  import EmptyState from '../../EmptyState.svelte';
  import ErrorState from '../../ErrorState.svelte';

  // Preferences manager
  const preferences = createPreferencesManager('employer-management');

  // When true, render the focused "Verification" view: only employers pending
  // registration (invited but not yet completed), with filters/metrics/add hidden.
  export let pendingOnly = false;
  
  // State
  let loading = false;
  let error: string | null = null;
  let employers: Employer[] = [];
  let totalPages = 1;
  let totalItems = 0;

  // Metrics (from API summary — not current page)
  let totalEmployers = 0;
  let seededEmployers = 0;
  let activeEmployers = 0;
  let totalDonations = 0;

  // Filter states - initialize from preferences
  let searchPhrase = preferences.getFilters().searchPhrase || '';
  let selectedStatus = preferences.getFilters().selectedStatus || ''; // Filter by approved/unapproved
  let sortOption = preferences.getFilters().sortOption || 'newest';
  
  // Pagination - initialize from preferences
  let currentPage = preferences.getCurrentPage() || 1;
  let itemsPerPage = preferences.getPageSize() || 15;

  // Modal state
  let showAddModal = false;
  let employerToEdit: Employer | null = null;
  let showDeleteModal = false;
  let employerToDelete: Employer | null = null;
  let deleteLoading = false;

  // Status options
  const statusOptions = [
    { uuid: '', name: 'All Statuses' },
    { uuid: 'approved', name: 'Approved' },
    { uuid: 'seeded', name: 'Seeded' },
    { uuid: 'invited', name: 'Invited' },
    { uuid: 'unapproved', name: 'Unapproved' },
    { uuid: 'rejected', name: 'Rejected' }
  ];

  // Sort options
  const sortOptions = [
    { uuid: 'newest', name: 'Newest first' },
    { uuid: 'oldest', name: 'Oldest first' },
    { uuid: 'name_asc', name: 'Name A–Z' },
    { uuid: 'name_desc', name: 'Name Z–A' }
  ];

  // Table columns
  const columns = [
    {
      key: 'name',
      label: 'Employer Name',
      width: '22%',
      render: (value: string, row: Employer) => `<span class="employer-name">${value}</span>`
    },
    {
      key: 'abn',
      label: 'ABN',
      width: '10%',
      render: (value: string) => value || '—'
    },
    {
      key: 'employee_count',
      label: 'Employees',
      width: '8%',
      render: (value: number) => value?.toLocaleString() || '0'
    },
    {
      key: 'status',
      label: 'Status',
      width: '12%',
      align: 'center',
      render: (value: string) => {
        const check = '<svg class="status-icon" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path></svg>';
        const cross = '<svg class="status-icon" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path></svg>';
        const envelope = '<svg class="status-icon" viewBox="0 0 20 20" fill="currentColor"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path></svg>';
        const map: Record<string, { label: string; cls: string; icon: string }> = {
          approved: { label: 'Approved', cls: 'status-approved', icon: check },
          rejected: { label: 'Rejected', cls: 'status-rejected', icon: cross },
          seeded: { label: 'Seeded', cls: 'status-seeded', icon: cross },
          invited: { label: 'Invited', cls: 'status-invited', icon: envelope },
          unapproved: { label: 'Unapproved', cls: 'status-unapproved', icon: cross }
        };
        const m = map[value] || map.unapproved;
        return `<span class="status-badge ${m.cls}">${m.icon}${m.label}</span>`;
      }
    },
    {
      key: 'contact_email',
      label: 'Contact Email',
      width: '18%',
      render: (value: string) => value ? `<a href="mailto:${value}" class="email-link">${value}</a>` : '—'
    },
    {
      key: 'contact_phone',
      label: 'Contact Phone',
      width: '10%',
      render: (value: string) => value || '—'
    },
    {
      key: 'head_office_location',
      label: 'Location',
      width: '10%',
      render: (value: string) => value || '—'
    },
    {
      key: 'created_at',
      label: 'Created on',
      width: '12%',
      render: (value: string | null | undefined) => (value ? employersService.formatDate(value) : 'N/A')
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '8%',
      render: (_value: unknown, row: Employer) => `<button type="button" class="action-delete-btn" data-uuid="${row.uuid}" data-name="${(row.name || '').replace(/"/g, '&quot;')}" title="Delete employer">Delete</button>`
    }
  ];

  async function loadEmployers() {
    loading = true;
    error = null;
    
    try {
      const filters: EmployerFilters = {
        page: currentPage,
        per_page: itemsPerPage
      };

      if (searchPhrase) {
        filters.phrase = searchPhrase;
      }

      if (selectedStatus) {
        filters.status = selectedStatus;
      }

      // Sort: map sortOption to sort_by and sort_direction
      switch (sortOption) {
        case 'oldest':
          filters.sort_by = 'created_at';
          filters.sort_direction = 'asc';
          break;
        case 'name_asc':
          filters.sort_by = 'name';
          filters.sort_direction = 'asc';
          break;
        case 'name_desc':
          filters.sort_by = 'name';
          filters.sort_direction = 'desc';
          break;
        default:
          filters.sort_by = 'created_at';
          filters.sort_direction = 'desc';
      }

      const response = await employersService.getEmployers(filters);
      
      if (response.success) {
        const rawData = response.data;
        const isPaginated = rawData && typeof rawData === 'object' && 'items' in rawData;
        employers = Array.isArray(rawData) ? rawData : (isPaginated && Array.isArray((rawData as { items?: Employer[] }).items) ? (rawData as { items: Employer[] }).items : []);
        const p = (response as { paging?: PaginationInfo }).paging ?? (isPaginated && rawData && typeof rawData === 'object' && 'paging' in rawData ? (rawData as { paging?: PaginationInfo }).paging : undefined);
        totalItems = (p && typeof p === 'object' ? p.total : undefined) ?? 0;
        totalPages = (p && typeof p === 'object' ? p.last_page : undefined) ?? 1;
        currentPage = apiPageToUiPage(p && typeof p === 'object' ? p.current_page : undefined);
        itemsPerPage = (p && typeof p === 'object' ? p.per_page : undefined) ?? 15;

        totalEmployers = totalItems;
        const summary = (response as { summary?: { total: number; seeded: number; approved: number } }).summary;
        if (summary) {
          totalEmployers = summary.total;
          seededEmployers = summary.seeded;
          activeEmployers = summary.approved;
        }
        totalDonations = 0;
      } else {
        error = response.message || 'Failed to load employers';
        toast.error(error);
        employers = [];
      }
    } catch (err: any) {
      error = err.message || 'Failed to load employers';
      toast.error(error);
      employers = [];
    } finally {
      loading = false;
    }
  }

  function handleSearch() {
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchPhrase, selectedStatus, sortOption } });
    loadEmployers();
  }

  function handleStatusChange(value: string) {
    selectedStatus = value;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchPhrase, selectedStatus, sortOption } });
    loadEmployers();
  }

  function handleSortChange(value: string) {
    sortOption = value;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchPhrase, selectedStatus, sortOption } });
    loadEmployers();
  }
  
  // Watch for filter changes and save preferences
  $: if (searchPhrase !== undefined || selectedStatus !== undefined || sortOption !== undefined) {
    preferences.updateFilters({ searchPhrase, selectedStatus, sortOption });
  }
  
  // Watch for page size changes
  $: if (itemsPerPage !== undefined) {
    preferences.update({ pageSize: itemsPerPage });
  }
  
  // Watch for page changes
  $: if (currentPage !== undefined) {
    preferences.update({ currentPage });
  }

  function handleResetFilters() {
    searchPhrase = '';
    selectedStatus = '';
    sortOption = 'newest';
    currentPage = 1;
    preferences.reset();
    loadEmployers();
  }

  function handlePageChange(event: CustomEvent<{ page: number }>) {
    currentPage = event.detail.page;
    preferences.update({ currentPage });
    loadEmployers();
  }

  function handleRowClick(row: Employer) {
    push(`/employer-profile/${row.uuid}`);
  }

  function handleAddEmployer() {
    employerToEdit = null;
    showAddModal = true;
  }

  function handleCloseModal() {
    showAddModal = false;
    employerToEdit = null;
  }

  async function handleSaveEmployer(data: any) {
    // The employer data is already saved by the modal component
    // We just need to reload the list
    toast.success(employerToEdit ? 'Employer updated successfully' : 'Employer added successfully');
    showAddModal = false;
    employerToEdit = null;
    loadEmployers(); // Reload the list
  }

  function openDeleteModal(employer: Employer) {
    employerToDelete = employer;
    showDeleteModal = true;
  }

  function closeDeleteModal() {
    if (!deleteLoading) {
      showDeleteModal = false;
      employerToDelete = null;
    }
  }

  async function handleConfirmDeleteEmployer() {
    if (!employerToDelete) return;
    deleteLoading = true;
    try {
      await employersService.deleteEmployer(employerToDelete.uuid);
      toast.success('Employer deleted successfully');
      closeDeleteModal();
      loadEmployers();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete employer');
    } finally {
      deleteLoading = false;
    }
  }

  function handleTableClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const btn = target.closest('.action-delete-btn');
    if (btn && btn instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      const uuid = btn.dataset.uuid;
      const employer = employers.find(e => e.uuid === uuid);
      if (employer) openDeleteModal(employer);
    }
  }

  onMount(() => {
    if (pendingOnly) {
      // Verification view is locked to employers pending registration.
      selectedStatus = 'invited';
    } else {
      // Manage list always opens showing all employers (no remembered status filter).
      selectedStatus = '';
    }
    currentPage = 1;
    loadEmployers();
  });
</script>

<div class="manage-list-page" class:pending-only={pendingOnly}>
  <div class="page-header">
    <p class="subtitle">
      {pendingOnly ? 'Employers invited but not yet registered' : 'Manage and monitor all employers across the platform'}
    </p>
  </div>

  {#if !pendingOnly}
  <div class="metrics-grid">
      <div class="metric-card">
        <div class="metric-label">Total Employers</div>
        <div class="metric-value">{totalEmployers.toLocaleString()}</div>
      </div>
      <div class="metric-card">
        <div class="metric-label">Seeded</div>
        <div class="metric-value">{seededEmployers.toLocaleString()}</div>
      </div>
      <div class="metric-card">
        <div class="metric-label">Registered</div>
        <div class="metric-value">{activeEmployers.toLocaleString()}</div>
      </div>
      <div class="metric-card">
        <div class="metric-label">Total Donations</div>
        <div class="metric-value">${totalDonations.toLocaleString()}</div>
      </div>
    </div>
  {/if}

  {#if !pendingOnly}
  <div class="filters-section">
    <div class="filters-left">
      <div class="filter-item">
        <SearchInput
          bind:value={searchPhrase}
          placeholder="Search employers..."
          on:search={handleSearch}
          width="100%"
        />
      </div>
      
      <div class="filter-item">
        <Dropdown
          options={statusOptions}
          selectedValue={selectedStatus}
          placeholder="All Statuses"
          onSelectionChange={handleStatusChange}
        />
      </div>
      <div class="filter-item">
        <Dropdown
          options={sortOptions}
          selectedValue={sortOption}
          placeholder="Sort by"
          onSelectionChange={handleSortChange}
        />
      </div>
    </div>
    
    <div class="filters-right">
      <Button variant="primary" on:click={handleAddEmployer}>
        Add Employer
      </Button>
      {#if searchPhrase || selectedStatus || sortOption !== 'newest'}
        <Button variant="secondary" on:click={handleResetFilters}>
          Reset Filters
        </Button>
      {/if}
    </div>
  </div>
  {/if}

  <!-- Employers Table Section -->
  <div class="table-section">
    {#if error}
      <ErrorState 
        message="Failed to load employers" 
        description={error}
        onRetry={loadEmployers}
      />
    {:else if loading}
      <div class="loading-wrap">
        <LoadingSkeleton lines={10} height="2.5rem" />
      </div>
    {:else if !employers || employers.length === 0}
      <EmptyState 
        message="No employers found"
        description={searchPhrase || selectedStatus || sortOption !== 'newest' ? "Try adjusting your filters to see more results." : "Get started by adding your first employer."}
        actionLabel={searchPhrase || selectedStatus || sortOption !== 'newest' ? "Reset Filters" : null}
        onAction={searchPhrase || selectedStatus || sortOption !== 'newest' ? handleResetFilters : null}
      />
    {:else}
      <div class="table-container" on:click={handleTableClick} role="presentation">
        <Table
          columns={columns}
          data={employers}
          onRowClick={handleRowClick}
        />
      </div>

      {#if totalPages > 1}
        <div class="pagination-container">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            on:pageChange={handlePageChange}
          />
        </div>
      {/if}
    {/if}
  </div>
</div>

<!-- Add/Edit Employer Modal -->
<AddEmployerModal
  open={showAddModal}
  employer={employerToEdit}
  onClose={handleCloseModal}
  onSave={handleSaveEmployer}
/>

<!-- Delete Employer confirmation modal -->
{#if showDeleteModal && employerToDelete}
  <div class="modal-overlay" role="dialog" aria-labelledby="delete-employer-modal-title" aria-modal="true">
    <div class="modal-content confirm-modal">
      <h3 id="delete-employer-modal-title">Delete employer?</h3>
      <p>This will permanently remove <strong>{employerToDelete.name}</strong> from the system. This action cannot be undone.</p>
      <div class="modal-actions">
        <Button variant="secondary" on:click={closeDeleteModal} disabled={deleteLoading}>Cancel</Button>
        <Button variant="primary" on:click={handleConfirmDeleteEmployer} disabled={deleteLoading} class="delete-confirm-button">
          {deleteLoading ? 'Deleting…' : 'Delete'}
        </Button>
      </div>
    </div>
  </div>
{/if}

<style>
  .page-header {
    margin-bottom: 1.5rem;
  }

  .subtitle {
    margin: 0;
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.5;
  }

  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .metric-card {
    background: white;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 1rem 1.25rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    min-width: 0;
  }

  .metric-label {
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
    margin: 0;
  }

  .metric-value {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
  }

  .filters-section {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1rem;
    width: 100%;
  }

  .filters-left {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    flex: 1;
    min-width: 0;
    align-items: flex-start;
    width: 100%;
  }

  .filters-right {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    flex-shrink: 0;
  }

  .filter-item {
    flex: 1 1 auto;
    min-width: 140px;
    max-width: 300px;
  }

  .filter-item :global(.search-input-wrapper),
  .filter-item :global(.dropdown-wrapper) {
    width: 100%;
  }

  .table-section {
    background: white;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    overflow: hidden;
  }

  .loading-wrap {
    padding: 2rem;
    min-height: 320px;
  }

  .pagination-container {
    padding: 1rem 1.5rem;
    border-top: 1px solid #f3f4f6;
  }

  .table-container {
    overflow-x: auto;
  }

  :global(.action-delete-btn) {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
    color: var(--color-negative, #dc2626);
    background: transparent;
    border: 1px solid var(--color-negative, #dc2626);
    border-radius: 6px;
    cursor: pointer;
  }
  :global(.action-delete-btn:hover) {
    background: rgba(220, 38, 38, 0.08);
  }

  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
  }
  .modal-content.confirm-modal {
    background: white;
    border-radius: 10px;
    padding: 1.5rem;
    max-width: 420px;
    width: 90%;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
  .modal-content.confirm-modal h3 {
    margin: 0 0 0.75rem 0;
    font-size: 1.125rem;
  }
  .modal-content.confirm-modal p {
    margin: 0 0 1.25rem 0;
    font-size: 0.875rem;
    color: #374151;
  }
  .modal-actions {
    display: flex;
    gap: 0.75rem;
    justify-content: flex-end;
  }
  /* Table cell styles */
  :global(.employer-name) {
    font-weight: 500;
    color: #111827;
  }

  :global(.email-link) {
    color: var(--color-primary, #f59e0b);
    text-decoration: none;
  }

  :global(.email-link:hover) {
    text-decoration: underline;
  }

  :global(.status-badge) {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
  }

  :global(.status-icon) {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  :global(.status-approved) {
    background-color: #dcfce7;
    color: #166534;
  }

  :global(.status-unapproved) {
    background-color: #fef3c7;
    color: #92400e;
  }

  :global(.status-seeded) {
    background-color: #dbeafe;
    color: #1e40af;
  }

  :global(.status-rejected) {
    background-color: #fee2e2;
    color: #991b1b;
  }

  :global(.status-invited) {
    background-color: #eef2ff;
    color: #4338ca;
  }

  :global(.edit-button) {
    padding: 0.375rem 0.75rem;
    background-color: var(--color-primary, #f59e0b);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  :global(.edit-button:hover) {
    background-color: var(--color-primary-hover, #f0a800);
  }

  :global(.edit-button:active) {
    transform: translateY(1px);
  }

  /* Responsive Design */
  @media (max-width: 1200px) {
    .filters-section {
      flex-wrap: wrap;
    }

    .filters-left {
      flex: 1 1 100%;
      min-width: 0;
    }

    .filters-right {
      flex: 0 0 auto;
      width: 100%;
      justify-content: flex-start;
      margin-top: 0.5rem;
    }
  }

  @media (max-width: 1024px) {
    .metrics-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 768px) {
    .page-header {
      flex-direction: column;
      align-items: stretch;
    }

    .metrics-grid {
      grid-template-columns: 1fr;
    }

    .filters-section {
      flex-direction: column;
      align-items: stretch;
    }

    .filters-left {
      flex-direction: column;
      width: 100%;
      max-width: none;
    }

    .filter-item {
      width: 100%;
      min-width: 0;
      max-width: none;
    }

    .filters-right {
      width: 100%;
      flex-direction: column;
    }

    .filters-right > :global(*) {
      width: 100%;
    }

    .table-section {
      overflow-x: auto;
    }
  }

  @media (max-width: 480px) {
    .metric-value {
      font-size: 1.25rem;
    }
  }
</style>

